type TripRequestItemTraveller = App.BusinessTraveller.TripRequestItemTraveller
type FlightHeldSeat = App.BusinessTraveller.FlightHeldSeat

/** The flight and seat number every seat shape carries. */
export type FlightSeatRef = Pick<App.BusinessTraveller.FlightTripRequestSeatSelection, 'flightNumber' | 'seatNumber'>

export function isSameSeat(a: FlightSeatRef, b: FlightSeatRef): boolean {
  return a.flightNumber === b.flightNumber && a.seatNumber === b.seatNumber
}

/** True when the held PNR is known to carry the seat. Unread held seats hold nothing. */
export function isSeatHeld(seat: FlightSeatRef, heldSeats: Array<FlightHeldSeat> | undefined): boolean {
  return !!heldSeats?.some(held => isSameSeat(held, seat))
}

/** `QF-490 22B`, how a seat is named to the customer. */
export function formatSeatLabel(seat: FlightSeatRef): string {
  return `${seat.flightNumber} ${seat.seatNumber}`
}

/** The seats every held item's PNR holds, or undefined while any of them is still unread. */
export function readHeldSeats(heldItems: Array<App.BusinessTraveller.FlightTripRequestItem>): Array<FlightHeldSeat> | undefined {
  const readings = heldItems.map(item => item.details?.heldSeats)
  return readings.some(seats => !seats) ? undefined : readings.flatMap(seats => seats ?? [])
}

/** Flattens an item's traveller seat selections into seat numbers and a total cost, skipping seats the held PNR dropped. */
export function getSeatSelectionsSummary(travellers: Array<TripRequestItemTraveller> = [], heldSeats?: Array<FlightHeldSeat>) {
  const requestedSeats = travellers.flatMap(traveller => traveller.guestTravellerDetails?.seatSelections ?? [])
  const seats = requestedSeats.filter(seat => !heldSeats || isSeatHeld(seat, heldSeats))
  return {
    requestedCount: requestedSeats.length,
    seatNumbers: seats.map(seat => seat.seatNumber),
    totalCost: seats.reduce((total, seat) => total + seat.pricesByPassengerType.totalAmount.amount, 0),
  }
}
